import { Check, ChevronDown, LoaderCircle, Search } from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDismiss } from "@/shared/hooks/useDismiss";

/**
 * Escolha ÚNICA com busca — pra caixa cuja lista completa não cabe.
 *
 * Um `<select>` nativo mostra o que recebeu e só. Quando a lista vem
 * de um ranking com teto (as 50 empresas que mais gastaram) ou de um
 * catálogo de milhares (12 mil empresas), quem não está na lista não
 * pode ser escolhido — e a pessoa não sabe que ele existe. Pedido da
 * dona (2026-09-14): a caixa abre com o que já tem e, quando se
 * digita, busca no servidor TUDO que casa. É a mesma forma do
 * `MultiSelectFilter` (um `<details>` com lupa e chips), reduzida a
 * uma escolha só, pra platafoma continuar com um modelo de filtro.
 *
 * Duas regras de comportamento:
 *
 * - **Sem texto, mostra `options`** (o ranking, o que a tela já tinha)
 *   e não pede nada ao servidor — abrir a caixa não custa requisição.
 * - **Com texto, `search(q)` responde**, com atraso de 300ms depois
 *   da última tecla e descarte por CARGA (um contador): a resposta de
 *   "sa" que chega depois da de "sao" é jogada fora, senão a lista
 *   piscaria pro resultado velho. Sem `search`, filtra `options`
 *   localmente — serve pra lista curta que só quer a lupa.
 *
 * O valor escolhido pode não estar em `options` (veio de uma busca, ou
 * da URL): `valueLabel` é o que aparece no cabeçalho nesse caso, pra
 * caixa nunca dizer "Todas" com um filtro aplicado.
 *
 * `required` tira a opção de limpar — é a caixa de RELATÓRIO do
 * dashboard, onde "nenhum" não é escolha. `group` nas opções vira um
 * título entre elas (Salvos / Rascunhos), na ordem em que aparecem.
 */

export type SearchSelectOption = { value: string; label: string; group?: string };

const SEARCH_DELAY_MS = 300;

/** O primeiro ancestral que RECORTA o que transborda (`overflow` que não
 *  é `visible`) — a "folha" de Ajustes, o `.content` que rola — ou o
 *  documento, quando nenhum recorta. */
function clippingAncestor(element: HTMLElement): HTMLElement {
	let node = element.parentElement;
	while (node && node !== document.body) {
		const { overflow, overflowX } = getComputedStyle(node);
		if ((overflowX || overflow) && overflowX !== "visible" && overflow !== "visible") return node;
		node = node.parentElement;
	}
	return document.documentElement;
}

/**
 * A lista nasce alinhada à esquerda do gatilho e, quando isso a faria
 * passar da borda do ancestral que recorta, vira pra direita (2026-10-17).
 * Era o caso de "Empresa" no consumo de IA: 360px de lista, o gatilho a
 * 33px da borda da folha, e o lado direito dos nomes sumia sem aviso.
 * A conta usa a posição do GATILHO e a largura da lista, não a posição
 * atual da lista — assim não depende do lado em que ela abriu da última
 * vez. Se não cabe de nenhum lado, fica à esquerda, que é o padrão.
 */
export function shouldFlipToTheLeft(trigger: HTMLElement, body: HTMLElement): boolean {
	const clip = clippingAncestor(trigger).getBoundingClientRect();
	const at = trigger.getBoundingClientRect();
	const width = body.offsetWidth;
	if (!width) return false;
	const overflowsRight = at.left + width > clip.right;
	const fitsLeft = at.right - width >= clip.left;
	return overflowsRight && fitsLeft;
}

export function SearchSelect({
	label,
	value,
	valueLabel,
	options,
	onChange,
	search,
	allLabel = "Todas",
	required = false,
	disabled,
	disabledReason
}: {
	label: string;
	/** `""` é "sem filtro". */
	value: string;
	/** Rótulo do escolhido quando ele não está em `options`. */
	valueLabel?: string;
	/** O que a caixa mostra sem texto digitado. */
	options: SearchSelectOption[];
	onChange: (value: string) => void;
	/** Busca no servidor; ausente, filtra `options` localmente. */
	search?: (query: string) => Promise<SearchSelectOption[]>;
	allLabel?: string;
	/** Sem a opção de limpar: escolher é obrigatório. */
	required?: boolean;
	disabled?: boolean;
	/** Por que está desabilitada — aparece no lugar da lista. */
	disabledReason?: string;
}) {
	const [term, setTerm] = useState("");
	const [results, setResults] = useState<SearchSelectOption[] | null>(null);
	const [searching, setSearching] = useState(false);
	const [failed, setFailed] = useState(false);
	const detailsRef = useRef<HTMLDetailsElement>(null);
	const bodyRef = useRef<HTMLDivElement>(null);
	const [flipped, setFlipped] = useState(false);
	// Espelho do `open` do `<details>`, que é quem manda: existe só pra
	// armar o clique-fora e o Esc enquanto a lista está aberta. O
	// `<details>` nativo não fecha sozinho com clique fora, e a lista
	// ficava aberta por cima da tela (2026-10-21).
	const [open, setOpen] = useState(false);
	const loadRef = useRef(0);
	const slug = label.toLocaleLowerCase("pt-BR").replaceAll(" ", "-");
	const query = term.trim();

	useEffect(() => {
		if (!search || !query) {
			setResults(null);
			setSearching(false);
			return;
		}
		const load = ++loadRef.current;
		setSearching(true);
		const timer = setTimeout(() => {
			search(query)
				.then(found => {
					if (loadRef.current !== load) return;
					setResults(found);
					setFailed(false);
				})
				.catch(() => {
					if (loadRef.current !== load) return;
					setResults([]);
					setFailed(true);
				})
				.finally(() => {
					if (loadRef.current === load) setSearching(false);
				});
		}, SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [search, query]);

	const shown = search
		? (results ?? options)
		: options.filter(option => option.label.toLocaleLowerCase("pt-BR").includes(query.toLocaleLowerCase("pt-BR")));
	const current = value
		? (options.find(option => option.value === value)?.label ?? valueLabel ?? value)
		: allLabel;

	const close = useCallback(() => {
		if (detailsRef.current) detailsRef.current.open = false;
	}, []);
	useDismiss(open, close, { ref: detailsRef });

	/** Decide o lado a cada abertura — a janela pode ter mudado. */
	function handleToggle() {
		const details = detailsRef.current;
		const body = bodyRef.current;
		setOpen(Boolean(details?.open));
		if (!details || !body || !details.open) return;
		setFlipped(shouldFlipToTheLeft(details, body));
	}

	function choose(next: string) {
		onChange(next);
		setTerm("");
		close();
	}

	if (disabled) {
		return (
			<details className="multi-filter is-unavailable">
				<summary>
					<Search size={15} aria-hidden="true"/>
					<span>{label}</span>
					<small>{allLabel}</small>
					<ChevronDown size={16} aria-hidden="true"/>
				</summary>
				{disabledReason && (
					<div className="multi-filter-body">
						<p className="multi-filter-hint">{disabledReason}</p>
					</div>
				)}
			</details>
		);
	}

	return (
		<details
			className={`multi-filter search-select${flipped ? " is-flipped" : ""}`}
			ref={detailsRef}
			onToggle={handleToggle}
		>
			<summary>
				<Search size={15} aria-hidden="true"/>
				<span>{label}</span>
				<small className={value ? "is-set" : undefined} title={current}>{current}</small>
				<ChevronDown size={16} aria-hidden="true"/>
			</summary>
			<div className="multi-filter-body" ref={bodyRef}>
				<div className="multi-filter-search">
					{searching ? <LoaderCircle size={15} className="spin" aria-hidden="true"/> : <Search size={15} aria-hidden="true"/>}
					<input
						type="search"
						name={`search-select-${slug}`}
						value={term}
						onChange={event => setTerm(event.target.value)}
						placeholder={search ? `Digite para buscar em ${label}…` : `Buscar em ${label}…`}
						aria-label={`Buscar opções de ${label}`}
						autoComplete="off"
						spellCheck={false}
					/>
				</div>
				{failed && <p className="multi-filter-hint" role="alert">Não foi possível buscar. Tente de novo.</p>}
				<div className="multi-filter-options filter-chip-group" role="listbox" aria-label={label}>
					{!required && (
						<button
							type="button"
							role="option"
							className={`filter-chip ${value ? "" : "active"}`}
							aria-selected={!value}
							onClick={() => choose("")}
						>
							{!value && <Check size={13} aria-hidden="true"/>}
							{allLabel}
						</button>
					)}
					{value && !shown.some(option => option.value === value) && (
						<button type="button" role="option" className="filter-chip active" aria-selected onClick={() => choose(value)}>
							<Check size={13} aria-hidden="true"/>
							<span className="filter-chip-label">{current}</span>
						</button>
					)}
					{shown.map((option, index) => (
						<Fragment key={option.value}>
							{option.group && option.group !== shown[index - 1]?.group && (
								<p className="search-select-group" role="presentation">{option.group}</p>
							)}
							<button
								type="button"
								role="option"
								className={`filter-chip ${option.value === value ? "active" : ""}`}
								aria-selected={option.value === value}
								onClick={() => choose(option.value)}
							>
								{option.value === value && <Check size={13} aria-hidden="true"/>}
								<span className="filter-chip-label">{option.label}</span>
							</button>
						</Fragment>
					))}
					{query && !searching && shown.length === 0 && !failed && (
						<p className="multi-filter-hint">Nada casa com "{query}".</p>
					)}
				</div>
			</div>
		</details>
	);
}
